import {useState, useCallback} from 'react';

/**
 * Hook personnalisé pour gérer les états des dialogues et leur réinitialisation
 * @param {Object} initialDialogStates - États initiaux des dialogues (ex: { editDialog: false })
 * @param {Object} resettersConfig - Configuration des fonctions de réinitialisation pour chaque dialogue
 * @param {Object} options - Options supplémentaires pour le comportement des dialogues
 * @return {Object} - Contient les états des dialogues, les fonctions pour ouvrir/fermer et réinitialiser
 */
export const useDialogControl = (
    initialDialogStates = {},
    resettersConfig = {},
    options = {
      disableBackdropClick: false, // Par défaut, on autorise la fermeture en cliquant sur le backdrop
    },
) => {
  const [dialogStates, setDialogStates] = useState(initialDialogStates);
  const [disableBackdropClick, setDisableBackdropClick] = useState(options.disableBackdropClick);

  /**
   * Ouvre un dialogue spécifique
   * @param {string} dialogName - Nom du dialogue à ouvrir
   * @param {Object} dialogOptions - Options spécifiques pour ce dialogue
   */
  const openDialog = useCallback((dialogName, dialogOptions = {}) => {
    // On peut éventuellement surcharger les options par dialogue
    if (dialogOptions.disableBackdropClick !== undefined) {
      setDisableBackdropClick(dialogOptions.disableBackdropClick);
    }

    setDialogStates((prevState) => ({
      ...prevState,
      [dialogName]: true,
    }));
  }, []);

  /**
   * Ferme un dialogue spécifique et réinitialise ses champs associés
   * @param {string} dialogName - Nom du dialogue à fermer
   */
  const closeDialog = useCallback((dialogName) => {
    // Ferme le dialogue
    setDialogStates((prevState) => ({
      ...prevState,
      [dialogName]: false,
    }));

    // Exécute les fonctions de réinitialisation associées à ce dialogue
    const resetters = resettersConfig[dialogName] || [];
    resetters.forEach((resetter) => {
      if (typeof resetter.reset === 'function') {
        resetter.reset();
      } else if (typeof resetter.setter === 'function' && 'value' in resetter) {
        resetter.setter(resetter.value);
      }
    });
  }, [resettersConfig]);

  /**
   * Gestionnaire pour l'événement onClose du Dialog
   * Empêche la fermeture si disableBackdropClick est true et que reason est "backdropClick"
   */
  const handleDialogClose = useCallback((dialogName, reason) => {
    // Si disableBackdropClick est true et que la raison est backdropClick, on ne fait rien
    if (disableBackdropClick && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }

    // Sinon, on ferme normalement
    closeDialog(dialogName);
  }, [closeDialog, disableBackdropClick]);

  /**
   * Vérifie si un dialogue est ouvert
   * @param {string} dialogName - Nom du dialogue à vérifier
   * @returns {boolean} - True si le dialogue est ouvert, false sinon
   */
  const isDialogOpen = useCallback((dialogName) => {
    return !!dialogStates[dialogName];
  }, [dialogStates]);

  return {
    dialogStates,
    openDialog,
    closeDialog,
    handleDialogClose,
    isDialogOpen,
    disableBackdropClick,
  };
};
